import axios, { CancelTokenSource } from 'axios';
import type {
  Brief,
  ResearchResult,
  Article,
  ImagePrompt,
  ArticleTypeId
} from '../types';
import { parseWebhookData, safeRender } from '../utils/webhookParser';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// ===============================
// CANCEL TOKEN MANAGEMENT
// ===============================
// Store active cancel tokens for cancelable operations
const activeRequests: Map<string, CancelTokenSource> = new Map();

// Create a cancel token for a specific operation
const createCancelToken = (operationId: string): CancelTokenSource => {
  // Cancel any existing request with the same ID
  cancelRequest(operationId);

  const source = axios.CancelToken.source();
  activeRequests.set(operationId, source);
  return source;
};

// Cancel a specific request
const cancelRequest = (operationId: string): boolean => {
  const source = activeRequests.get(operationId);
  if (source) {
    source.cancel(`Operation ${operationId} cancelled by user`);
    activeRequests.delete(operationId);
    return true;
  }
  return false;
};

// Check if a request was cancelled
const isRequestCancelled = (error: any): boolean => {
  return axios.isCancel(error);
};

// Export cancel functions for use in components
export const apiCancelTokens = {
  cancelResearch: () => cancelRequest('research'),
  cancelArticleGeneration: () => cancelRequest('article-generation'),
  cancelTopicGeneration: () => cancelRequest('topic-generation'),
  cancelAll: () => {
    activeRequests.forEach((source, id) => {
      source.cancel(`Operation ${id} cancelled`);
    });
    activeRequests.clear();
  },
  isActive: (operationId: string) => activeRequests.has(operationId),
};

// Interceptor para logging y autenticación
api.interceptors.request.use(
  (config) => {
    // Agregar token de autenticación si existe (desde sessionStorage)
    const token = sessionStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores 401 (token inválido)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Limpiar todo el localStorage y sessionStorage
      localStorage.clear();
      sessionStorage.clear();
      // Recargar la página para mostrar el login
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// ===============================
// LINK EXTRACTION SERVICE
// ===============================
export interface LinkedUrl {
  url: string;
  title: string;
  description: string;
  keywords: string[];
  ogImage?: string | null;
  extractedAt: string;
}

export const extractLinkInfo = async (url: string): Promise<LinkedUrl> => {
  try {
    const response = await api.post('/extract-link-info', { url });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error al extraer información del enlace');
  }
};

export const webhookService = {
  // Generar estrategia de contenido (competencia/keywords)
  async generateContentStrategy(data: any, sessionId?: string) {
    const cancelSource = createCancelToken('topic-generation');
    try {
      const payload = sessionId ? { ...data, session_id: sessionId } : data;

      // Usar endpoint local que conecta con Perplexity
      const response = await api.post('/generate-strategy', payload, {
        cancelToken: cancelSource.token
      });

      return response.data;
    } catch (error) {
      if (isRequestCancelled(error)) {
        throw new Error('CANCELLED');
      }
      throw error;
    } finally {
      activeRequests.delete('topic-generation');
    }
  },

  async importSemrushCompetitors(domain: string, sessionId?: string) {
    try {
      const response = await api.post('/semrush-competitors', { domain, sessionId });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Guardar temas seleccionados
  async saveTopics(projectId: string, topics: any[]) {
    try {
      const response = await api.post(`/projects/${projectId}/topics`, { topics });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener competidores de un proyecto
  async getProjectCompetitors(projectId: string): Promise<{ id: string; url: string }[]> {
    try {
      const response = await api.get(`/projects/${projectId}/competitors`);
      return response.data;
    } catch (error) {
      console.error('[API] Error getting competitors:', error);
      return [];
    }
  },

  // Añadir competidores (solo nuevos)
  async addProjectCompetitors(projectId: string, urls: string[]) {
    try {
      const response = await api.post(`/projects/${projectId}/competitors`, { urls });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Eliminar competidor
  async deleteProjectCompetitor(projectId: string, url: string) {
    try {
      const response = await api.delete(`/projects/${projectId}/competitors`, { data: { url } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ===== PROJECT KEYWORDS (TARGET) =====

  // Añadir keywords objetivo
  async addProjectKeywords(projectId: string, keywords: string[]) {
    try {
      const response = await api.post(`/projects/${projectId}/keywords`, { keywords });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener keywords objetivo
  async getProjectKeywords(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/keywords`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Eliminar keyword objetivo
  async deleteProjectKeyword(projectId: string, keyword: string) {
    try {
      const response = await api.delete(`/projects/${projectId}/keywords`, { data: { keyword } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ===== PALABRAS NEGATIVAS =====

  // Añadir palabras negativas
  async addNegativeKeywords(projectId: string, keywords: Array<{ keyword: string; type: 'word' | 'phrase' | 'topic' }>) {
    try {
      const response = await api.post(`/projects/${projectId}/negative-keywords`, { keywords });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener palabras negativas
  async getNegativeKeywords(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/negative-keywords`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Eliminar palabra negativa
  async deleteNegativeKeyword(projectId: string, keyword: string) {
    try {
      const response = await api.delete(`/projects/${projectId}/negative-keywords`, { data: { keyword } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Analizar estilo de escritura
  // Para tipos 'standard' y 'news': usa OpenAI GPT-4.1-mini directamente
  // Para otros tipos: usa webhook n8n
  // projectId: necesario para obtener credenciales de WordPress y extraer contenido completo
  async analyzeStyle(posts: any[], sessionId?: string, articleType?: string, projectId?: string) {
    try {
      // Todos los tipos usan OpenAI directo via backend (ya no usamos webhook n8n)
      const response = await api.post('/analyze-style-openai', {
        posts,
        sessionId,
        articleType,
        projectId // Necesario para obtener contenido completo de WordPress
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Analizar estilo desde URLs externas
  // Usa web-extractor + OpenAI GPT-4.1-mini
  // includeStructure: true = estilo + estructura detallada, false = solo estilo
  async analyzeStyleFromExternalUrls(urls: string[], sessionId?: string, includeStructure: boolean = false) {
    try {
      const response = await api.post('/analyze-style-external', {
        urls,
        sessionId,
        includeStructure
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Chat para briefing conversacional (Subtemas)
  async sendChatMessage(userResponse: string, sessionId: string) {
    try {
      // USAR PROXY INTERNO para evitar CORS
      const response = await api.post('/webhook/subtopics', {
        userResponse,
        sessionId
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener temas disponibles
  async getTopics(projectId?: string) {
    try {

      // GUARD: Don't fetch if no project ID
      if (!projectId) {
        return [];
      }

      const response = await api.get('/topics', {
        params: { projectId }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Marcar tema como publicado
  async markTopicAsPublished(topicId: string | number) {
    try {
      const response = await api.post(`/topics/${topicId}/publish`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Eliminar tema
  async deleteTopic(topicId: string | number) {
    try {
      const response = await api.delete(`/topics/${topicId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Generar estructura de brief
  async generateBrief(rawText: string, targetKeyword: string, locale = 'es-ES', sessionId?: string, articleType?: ArticleTypeId): Promise<Brief> {
    try {
      const response = await api.post('/brief', {
        rawText,
        targetKeyword,
        locale,
        sessionId,
        articleType: articleType || 'wikipedia'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Investigación por subtemas
  async conductResearch(subtopics: string[], mainTopic: string, sessionId?: string, articleType?: ArticleTypeId): Promise<ResearchResult[]> {
    const cancelSource = createCancelToken('research');

    try {
      const response = await api.post('/research', {
        subtopics,
        mainTopic,
        sessionId,
        articleType: articleType || 'wikipedia'
      }, {
        cancelToken: cancelSource.token
      });

      // Clean up the cancel token
      activeRequests.delete('research');

      return response.data;
    } catch (error) {
      activeRequests.delete('research');

      if (isRequestCancelled(error)) {
        throw new Error('CANCELLED');
      }

      throw error;
    }
  },

  // Extraer y resumir items de comparación
  async extractComparisonItems(
    items: Array<{
      url: string;
      title: string;
      source: 'wp-page' | 'wp-post' | 'external';
      wpId?: number;
      excerpt?: string;
      featuredImage?: string;
    }>,
    articleTitle: string,
    wpCredentials?: { url: string; user: string; appPassword: string } | null
  ): Promise<{
    success: boolean;
    items: Array<{
      url: string;
      title: string;
      source: string;
      summary: string;
      features: string[];
      suggestedAnchor: string;
      featuredImage?: string;
      extracted: boolean;
    }>;
  }> {
    try {
      const response = await api.post('/extract-comparison-items', {
        items,
        articleTitle,
        wpCredentials
      }, {
        timeout: 120000 // 2 minutos para extraccion
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Generar artículo
  async generateArticle(
    brief: Brief,
    researchData: ResearchResult[],
    additionalInstructions?: string,
    projectId?: string,
    keywords?: string[],
    styleAnalysis?: string,
    linkedPages?: string[],
    includeIndex?: string,
    faqDesplegable?: string,
    // Nuevos parámetros para tipos de artículo
    articleType?: ArticleTypeId,
    comparisonUrls?: string[],
    mainSourceUrl?: string,
    wordCount?: number,
    comparisonItems?: Array<{
      url: string;
      title: string;
      source: string;
      summary: string;
      features: string[];
      suggestedAnchor: string;
      featuredImage?: string;
    }>,
    // URLs externas con info scrapeada para enlaces internos
    linkedUrls?: LinkedUrl[],
    // CTA y modo título
    ctaText?: string,
    ctaUrl?: string,
    isTitleStrict?: boolean,
    // Términos de taxonomía con su tipo (para resolver el endpoint correcto)
    linkedTaxonomyTerms?: { id: string; taxonomySlug: string; restBase?: string }[],
  ): Promise<Article> {
    const cancelSource = createCancelToken('article-generation');

    try {
      const response = await api.post('/article', {
        brief,
        researchResults: researchData,
        additionalInstructions,
        projectId,
        keywords,
        styleAnalysis,
        linkedPages,
        linkedUrls, // URLs externas con info scrapeada
        linkedTaxonomyTerms, // Términos de taxonomía con tipo para resolver el endpoint correcto
        includeIndex,
        faqDesplegable,
        // Nuevos campos
        articleType: articleType || 'wikipedia',
        comparisonUrls: comparisonUrls || [],
        comparisonItems: comparisonItems || [],
        mainSourceUrl: mainSourceUrl || '',
        wordCount: wordCount || 2000,
        // CTA y modo título
        ctaText,
        ctaUrl,
        isTitleStrict
      }, {
        cancelToken: cancelSource.token,
        timeout: 300000 // 5 minutos timeout
      });

      // Si tenemos un topicId, marcar el tema como publicado
      // @ts-ignore - topicId might not be in the Brief type definition yet
      if (brief.topicId) {
        try {
          // @ts-ignore
          await webhookService.markTopicAsPublished(brief.topicId);
        } catch (error) {
          // No bloqueamos el flujo si esto falla
        }
      }

      // Helper: Inyectar schemas JSON-LD en el HTML si aún no están presentes
      const injectSchemasIntoHtml = (baseHtml: string, schemas: any): string => {
        try {
          let html = typeof baseHtml === 'string' ? baseHtml : safeRender(baseHtml);
          if (!schemas) return html;
          // Evitar duplicados si ya hay scripts JSON-LD
          if (html.includes('application/ld+json')) return html;

          const scripts: string[] = [];

          const pushScript = (obj: any) => {
            try {
              scripts.push('<script type="application/ld+json">\n' + JSON.stringify(obj, null, 2) + '\n</script>');
            } catch (_) {
              // ignore
            }
          };

          if (Array.isArray(schemas)) {
            schemas.forEach(s => pushScript(s));
          } else if (typeof schemas === 'object') {
            // Caso habitual con claves: blogPosting, faqPage, breadcrumbList
            const knownKeys = ['blogPosting', 'faqPage', 'breadcrumbList'];
            const hasKnown = knownKeys.some(k => schemas && schemas[k]);
            if (hasKnown) {
              knownKeys.forEach(k => schemas[k] && pushScript(schemas[k]));
            } else {
              // Objeto genérico: inyectar tal cual
              pushScript(schemas);
            }
          }

          if (scripts.length === 0) return html;

          const scriptTags = '\n' + scripts.join('\n') + '\n';
          const closeTag = '</article>';
          const closeIdx = html.lastIndexOf(closeTag);
          if (closeIdx !== -1) {
            return html.slice(0, closeIdx) + scriptTags + html.slice(closeIdx);
          }
          return html + scriptTags;
        } catch {
          return typeof baseHtml === 'string' ? baseHtml : safeRender(baseHtml);
        }
      };

      let articleData;

      // Intentar parsear con parseWebhookData primero
      const parsedWebhookData = parseWebhookData(response.data);

      if (parsedWebhookData) {
        // Extraer HTML de manera mas robusta
        let htmlContent = '';
        if (parsedWebhookData.articleHtml) {
          htmlContent = typeof parsedWebhookData.articleHtml === 'string'
            ? parsedWebhookData.articleHtml
            : safeRender(parsedWebhookData.articleHtml);
        } else if (parsedWebhookData.content) {
          htmlContent = typeof parsedWebhookData.content === 'string'
            ? parsedWebhookData.content
            : safeRender(parsedWebhookData.content);
        }
        // Inyectar schemas JSON-LD si vienen separados
        htmlContent = injectSchemasIntoHtml(htmlContent, parsedWebhookData.schemaJsonLd);

        articleData = {
          title: parsedWebhookData.h1 || parsedWebhookData.meta?.title || '',
          content: htmlContent,
          html: htmlContent,
          wordCount: parsedWebhookData.wordCount || 0,
          readingTime: parsedWebhookData.readingTime || 0,
          metadata: {
            metaTitle: parsedWebhookData.meta?.title || '',
            metaDescription: parsedWebhookData.meta?.description || '',
            keywords: parsedWebhookData.keywords || [],
          },
          schemaJsonLd: parsedWebhookData.schemaJsonLd || null,
          // IMPORTANTE: Preservar el tipo de artículo
          article_type: parsedWebhookData.article_type || articleType || 'wikipedia',
        };
      } else {
        // FALLBACK MEJORADO: Intentar extraer directamente de response.data
        let rawData: any = null;
        let rawHtml = '';
        let title = '';

        // Caso 1: NUEVO FORMATO - Array directo [{ meta, h1, articleHtml, ... }]
        if (Array.isArray(response.data) && response.data.length > 0) {
          rawData = response.data[0];
          rawHtml = rawData.articleHtml || rawData.content || '';
          title = rawData.h1 || rawData.meta?.title || '';
        }
        // Caso 2: Formato antiguo {articleData: {...}}
        else if (response.data?.articleData) {
          rawData = response.data.articleData;
          rawHtml = rawData.articleHtml || rawData.content || '';
          title = rawData.h1 || rawData.meta?.title || '';
        }
        // Caso 3: Wrapper { article: {...} }
        else if (response.data?.article) {
          rawData = response.data.article;
          rawHtml = rawData.articleHtml || rawData.html || rawData.content || '';
          title = rawData.h1 || rawData.title || rawData.meta?.title || '';
        }
        // Caso 4: Directo
        else {
          rawData = response.data;
          rawHtml = rawData?.articleHtml || rawData?.html || rawData?.content || '';
          title = rawData?.h1 || rawData?.title || rawData?.meta?.title || '';
        }

        // El HTML del nuevo formato YA VIENE LIMPIO, no necesita desescapado
        const computedKeywords = rawData?.keywordAnalysis?.primaryKeyword
          ? [
            rawData.keywordAnalysis.primaryKeyword,
            ...(rawData.keywordAnalysis.secondaryKeywords || [])
          ].filter(Boolean)
          : (rawData?.keywords || []);
        // Inyectar schemas JSON-LD si vienen separados del HTML
        rawHtml = injectSchemasIntoHtml(rawHtml, rawData?.schemaJsonLd);

        articleData = {
          title: title,
          content: rawHtml,
          html: rawHtml,
          wordCount: rawData?.keywordAnalysis?.totalWords || 0,
          readingTime: rawData?.keywordAnalysis?.totalWords
            ? Math.ceil(rawData.keywordAnalysis.totalWords / 200)
            : 0,
          metadata: {
            metaTitle: rawData?.meta?.title || '',
            metaDescription: rawData?.meta?.description || '',
            keywords: computedKeywords,
          },
          schemaJsonLd: rawData?.schemaJsonLd || null,
          // IMPORTANTE: Preservar el tipo de artículo
          article_type: rawData?.article_type || articleType || 'wikipedia',
        };
      }

      // Clean up the cancel token
      activeRequests.delete('article-generation');

      return articleData;
    } catch (error) {
      activeRequests.delete('article-generation');

      if (isRequestCancelled(error)) {
        throw new Error('CANCELLED');
      }

      throw error;
    }
  },

  // Generar prompts para imágenes
  async generateImagePrompts(articleContent: string, numberOfImages = 3): Promise<ImagePrompt[]> {
    try {
      const response = await api.post('/image-prompts', {
        articleContent,
        numberOfImages
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Generar imágenes con Gemini Nano Banana Pro (ultra realistas)
  async generateImages(
    tema: string,
    referenceImage?: string,
    researchSummary?: string,
    modificationPrompt?: string,
    mode: 'all' | 'images' | 'infographics' = 'all',
    sessionId?: string,
    customPrompt?: string, // Nuevo parámetro explícito para prompt_imagen
    numberOfImages: number = 3 // Cantidad de imágenes a generar
  ): Promise<{ images: string[]; infographics: string[]; imageCount: number; infographicCount: number }> {
    try {
      const response = await api.post('/generate-images', {
        tema,
        referenceImage,
        researchSummary,
        modificationPrompt,
        mode,
        sessionId,
        customPrompt,
        imageCount: numberOfImages // Cantidad de imagenes a generar
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener imágenes externas (BANCO DE IMAGENES - NUEVO)
  // page: página de resultados para paginación (evita descargar imágenes repetidas)
  async fetchExternalImages(tema: string, limit: number = 6, sessionId?: string, page: number = 1): Promise<any> {
    try {
      const response = await api.post('/fetch-external-images', { tema, limit, sessionId, page });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Health check
  async checkHealth() {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async optimizeForWordPress(data: { html: string; images: any; articleTitle: string; session_id?: string }) {
    try {
      // Ensure session_id is at root level for n8n compatibility
      const payload = {
        ...data,
        session_id: data.session_id,
        sessionId: data.session_id // compatibility
      };

      const response = await api.post('/optimize-wordpress', payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Generar metadatos de imagen con Gemini (title y alt SEO)
  async generateImageMetadata(
    articleTitle: string,
    articleContext: string,
    imageIndex: number = 1,
    imageSrc?: string
  ): Promise<{ success: boolean; title: string; alt: string }> {
    try {
      const response = await api.post('/generate-image-metadata', {
        articleTitle,
        articleContext,
        imageIndex,
        imageSrc
      });

      return response.data;
    } catch (error) {
      // Return fallback
      return {
        success: false,
        title: `${articleTitle} - Imagen ${imageIndex}`,
        alt: `Imagen ilustrativa sobre ${articleTitle}`
      };
    }
  },
};

// Servicio de proyectos
export const projectService = {
  // Obtener todos los proyectos
  async getProjects() {
    try {
      const response = await api.get('/projects');

      // Transformar los datos para asegurar el formato correcto
      const transformedProjects = response.data.map((project: any) => ({
        ...project,
        id: project.id || `project-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        createdAt: project.createdAt ? new Date(project.createdAt) : new Date(),
        updatedAt: project.updatedAt ? new Date(project.updatedAt) : new Date(),
        status: project.status || 'draft',
        briefingMode: project.briefingMode || null
      }));

      return transformedProjects;
    } catch (error) {
      throw error;
    }
  },

  // Obtener un proyecto especifico
  async getProject(id: string) {
    try {
      const response = await api.get(`/projects/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Crear un nuevo proyecto
  async createProject(name: string, description?: string, briefingMode?: 'chat' | 'form', url?: string, username?: string, password?: string, wordpress_connected?: boolean | null) {
    try {
      const response = await api.post('/projects', {
        name,
        description,
        briefingMode,
        url,
        username,
        password,
        wordpress_connected
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Actualizar un proyecto
  async updateProject(id: string, data: any) {
    try {
      const response = await api.put(`/projects/${id}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Guardar briefing data
  async saveBriefingData(projectId: string, briefingData: any) {
    try {
      const response = await api.post(`/projects/${projectId}/briefing`, { briefingData });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Eliminar articulo
  async deleteArticle(articleId: string | number) {
    try {
      const response = await api.delete(`/articles/${articleId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },


  // Guardar brief generado
  async saveBrief(projectId: string, brief: any) {
    try {
      const response = await api.post(`/projects/${projectId}/brief`, { brief });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Guardar resultados de investigacion
  async saveResearch(projectId: string, researchData: any) {
    try {
      const response = await api.post(`/projects/${projectId}/research`, { researchData });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Guardar articulo generado - usar webhook para consistencia
  async saveArticle(projectId: string, article: any) {
    try {
      const response = await api.post(`/projects/${projectId}/article`, article);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Actualizar un articulo existente
  async updateArticle(articleId: number, articleData: any) {
    try {
      const response = await api.put(`/articles/${articleId}`, articleData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Actualizar fecha programada de un articulo especifico
  async updateArticleSchedule(articleId: number, scheduledDateTime: string) {
    try {
      const response = await api.post(`/articles/${articleId}/schedule`, { scheduledDateTime });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Guardar imagenes de un proyecto
  async saveImages(projectId: string, images: string[]) {
    try {
      const response = await api.post(`/projects/${projectId}/images`, { images });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener SOLO credenciales de un proyecto
  async getProjectCredentials(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/credentials`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener posts de WordPress para analisis de estilo
  async getWordPressPosts(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/wordpress/posts`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener paginas de WordPress (Proxy)
  async getWordPressPages(projectId: string, search?: string) {
    try {
      const response = await api.get(`/projects/${projectId}/wordpress/pages`, {
        params: { search }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Detectar taxonomías disponibles en WordPress
  async getWordPressTaxonomies(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/wordpress/taxonomies`);
      return response.data;
    } catch (error) {
      console.error('Error fetching WordPress taxonomies:', error);
      throw error;
    }
  },

  // Obtener términos de una taxonomía específica (con paginación)
  async getWordPressTaxonomyTerms(projectId: string, taxonomySlug: string, search?: string, page: number = 1) {
    try {
      const response = await api.get(`/projects/${projectId}/wordpress/taxonomy/${taxonomySlug}`, {
        params: { search, page, per_page: 50 }
      });
      return response.data; // { items: [...], hasMore: boolean, total: number, page: number }
    } catch (error) {
      console.error(`Error fetching taxonomy terms for ${taxonomySlug}:`, error);
      throw error;
    }
  },

  // Obtener imagenes de la libreria de medios de WordPress (Proxy)
  async getWordPressMedia(projectId: string, page = 1, search = '') {
    try {
      const response = await api.get(`/projects/${projectId}/wordpress/media`, {
        params: { page, search }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },





  // Obtener categorias de WordPress usando Project ID (Proxy Backend)
  async getWordPressCategories(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/wordpress/categories`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener imagenes de un proyecto
  async getImages(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/images`);

      const images = response.data || [];
      if (!Array.isArray(images)) {
        return [];
      }

      return images;
    } catch (error: any) {
      throw error;
    }
  },

  // Obtener articulo de un proyecto
  async getProjectArticle(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/article`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Eliminar un proyecto
  async deleteProject(id: string) {
    try {
      const response = await api.delete(`/projects/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener todos los artículos generados de un proyecto
  async getProjectArticles(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/articles`);
      const data = response.data;
      const list = Array.isArray(data) ? data : (Array.isArray(data?.articles) ? data.articles : []);

      if (!list || list.length === 0) {
        return [];
      }

      // Los artículos ya vienen en el formato correcto desde el backend
      const articles = list.map((article: any) => ({
        id: article.id,  // ID del artículo en la base de datos
        session_id: `article-${projectId}-${article.id}`,
        project_id: projectId,
        article_data: article.article_data,
        title: article.title,
        word_count: article.word_count,
        status: 'completed',
        created_at: article.created_at,
        updated_at: article.updated_at,
        fecha_programado: article.fecha_programado,  // Fecha programada de publicación
        publicado: article.publicado || false,  // Estado de publicación
        subid_wordpress: article.subid_wordpress // Estado de subida a WordPress
      }));

      return articles;
    } catch (error: any) {
      // Si no hay articulos, devolver array vacio en lugar de error
      if (error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  // Obtener el ultimo articulo de un proyecto (para compatibilidad)
  async getLastProjectArticle(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/article`);

      if (!response.data) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // ===== SESSION IMAGES =====

  // Guardar imagenes de sesion (generadas o buscadas pero no insertadas)
  async saveSessionImages(
    projectId: string,
    sessionId: string,
    images: Array<{
      image_data: string;
      image_source: 'generated' | 'external' | 'uploaded';
      alt_text?: string;
      title?: string;
    }>
  ) {
    try {
      const response = await api.post(`/projects/${projectId}/session-images`, {
        sessionId,
        images
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener imagenes de sesion
  async getSessionImages(projectId: string, sessionId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/session-images`, {
        params: { sessionId }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener imagenes de un articulo especifico
  async getArticleImages(articleId: number) {
    try {
      const response = await api.get(`/articles/${articleId}/images`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Marcar imagen como insertada en articulo
  async markImageAsInserted(imageId: number, articleId: number) {
    try {
      const response = await api.post(`/images/${imageId}/mark-inserted`, { articleId });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ===== FAQ CONFIGURATION =====

  // Detectar formato de FAQ desde WordPress
  async detectFaqFormat(projectId: string) {
    try {
      const response = await api.post(`/projects/${projectId}/detect-faq`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener configuracion FAQ del proyecto
  async getFaqConfig(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/faq-config`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Actualizar configuracion FAQ
  async updateFaqConfig(projectId: string, faqFormat: string, faqTemplate?: string) {
    try {
      const response = await api.put(`/projects/${projectId}/faq-config`, {
        faq_format: faqFormat,
        faq_template: faqTemplate
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Probar conexion con WordPress
  async testWordPressConnection(url: string, username: string, password: string) {
    try {
      const response = await api.post('/wordpress/test-connection', {
        url,
        username,
        password
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Optimizar imagenes para descarga
  async optimizeImagesForDownload(images: Array<string | { url: string; name?: string }>) {
    try {
      const response = await api.post('/images/optimize-download', { images });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Servicio de sesiones
export const sessionService = {
  // Crear una nueva sesion
  async createSession(sessionId: string, projectId: string, briefingMode: 'chat' | 'form' | null) {
    try {
      const response = await api.post('/sessions', {
        sessionId,
        projectId,
        briefingMode
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default api;




